/**
 * FacetFiltersForm handles the filtering and sorting UI for product collections.
 * Manages dropdowns, filter forms, and updates to the product grid.
 * @extends HTMLElement
 */
class FacetFiltersForm extends HTMLElement {
    /**
     * Initializes event listeners and binds methods for the filter form and sort dropdown.
     */
    constructor() {
        super();
        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
        this.onSortButtonClick = this.onSortButtonClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.toggleDropdown = this.toggleDropdown.bind(this);
        this.sortDropdown = document.querySelector('#sortDropdown');
        this.debouncedOnSubmit = debounce((event) => {
            this.onSubmitHandler(event);
        }, 500);

        const facetForm = this.querySelector('form');
        facetForm.addEventListener('input', this.debouncedOnSubmit);
        facetForm.addEventListener('click', this.onDocumentClick);
        facetForm.addEventListener('change', this.onChange);

        this.sortDropdown.querySelectorAll('button').forEach((button) => {
            button.addEventListener('click', this.onSortButtonClick.bind(this));
        });
    }

    /**
     * Handles change events for filter checkboxes, updates analytics and UI state.
     * @param {Event} event
     */
    onChange(event) {
        if (event.target.matches('.mobile-facets__checkbox')) {
            const label = event.target.closest('label');
            const inputValue = label.querySelector('input');
            const inputFilterCount = inputValue.dataset.analyticsCount;
            sendAnalyticsEventData(USER_INTERACTION_ANALYTICS_EVENTS.filter, {
                count: inputFilterCount,
                title: inputValue.value,
            });
            event.target.checked ? label.classList.add('active') : label.classList.remove('active');
        }
    }

    /**
     * Handles document clicks to toggle or close the sort dropdown.
     * @param {Event} event
     */
    onDocumentClick(event) {
        if (
            event.target.matches('.facet-filters__label--sort, label[for="SortBy"], .facet-filters__label--sort .icon')
        ) {
            this.toggleDropdown(event);
        } else if (this.sortDropdown && !this.sortDropdown.contains(event.target)) {
            this.sortDropdown.classList.remove('sort-field--active');
            const sortButton = document.querySelector('.facet-filters__label--sort');
            if (sortButton) {
                sortButton.setAttribute('aria-expanded', 'false');
            }
            if (window.matchMedia('(max-width: 767px)')) {
                this.sortDropdown.style.maxHeight = '0px';
            }
        }
    }

    /**
     * Toggles the visibility of the sort dropdown and updates aria-expanded.
     * @param {Event} event
     */
    toggleDropdown(event) {
        event.preventDefault();
        const sortButton = document.querySelector('.facet-filters__label--sort');
        if (this.sortDropdown) {
            const isActive = this.sortDropdown.classList.contains('sort-field--active');
            if (window.matchMedia('(max-width: 767px)')) {
                this.sortDropdown.style.maxHeight = isActive ? '0px' : this.sortDropdown.scrollHeight + 'px';
            }
            this.sortDropdown.classList.toggle('sort-field--active');
            if (sortButton) {
                sortButton.setAttribute('aria-expanded', !isActive ? 'true' : 'false');
            }
        }
    }

    /**
     * Handles clicks on sort option buttons, applies the selected sort.
     * @param {Event} event
     */
    onSortButtonClick(event) {
        event.preventDefault();
        const button = event.target;
        const sortValue = button.dataset.value;

        this.sortDropdown.querySelectorAll('button').forEach((btn) => {
            btn.classList.remove('selected');
        });

        button.classList.add('selected');

        this.applySort(sortValue);
    }

    /**
     * Applies the selected sort and updates the product grid.
     * @param {string} sortValue
     */
    applySort(sortValue) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('sort_by', sortValue);
        FacetFiltersForm.renderPage(searchParams.toString());

        if (this.sortDropdown) {
            this.sortDropdown.classList.remove('sort-field--active');
            if (window.matchMedia('(max-width: 767px)')) {
                this.sortDropdown.style.maxHeight = '0px';
            }
        }
    }

    /**
     * Sets up popstate event listener for browser navigation.
     */
    static setListeners() {
        const onHistoryChange = (event) => {
            const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
            if (searchParams === FacetFiltersForm.searchParamsPrev) return;
            FacetFiltersForm.renderPage(searchParams, null, false);
        };
        window.addEventListener('popstate', onHistoryChange);
    }

    /**
     * Enables or disables all active facet remove buttons.
     * @param {boolean} [disable=true]
     */
    static toggleActiveFacets(disable = true) {
        document.querySelectorAll('.js-facet-remove').forEach((element) => {
            element.classList.toggle('disabled', disable);
        });
    }

    /**
     * Renders the product grid and filters for the given search parameters.
     * @param {string} searchParams
     * @param {Event} [event]
     * @param {boolean} [updateURLHash=true]
     * @param {string} [focusSelector=null]
     */
    static renderPage(searchParams, event, updateURLHash = true, focusSelector = null) {
        FacetFiltersForm.searchParamsPrev = searchParams;
        const sections = FacetFiltersForm.getSections();
        const mainCollectionContainer = document.getElementById('main-collection-product__container');
        const isMenuOpen = document.querySelector('.menu-opening');

        const loadingSpinner = document.querySelector('.global-loading');
        loadingSpinner.classList.remove('hidden');

        if (mainCollectionContainer) {
            if (!isMenuOpen) {
                mainCollectionContainer.classList.add('main-collection-product--loading');
            } else {
                mainCollectionContainer.classList.remove('main-collection-product--loading');
            }
        }

        sections.forEach((section) => {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const filterDataUrl = (element) => element.url === url;

            FacetFiltersForm.filterData.some(filterDataUrl)
                ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, focusSelector)
                : FacetFiltersForm.renderSectionFromFetch(url, event, focusSelector);
        });

        if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    }

    /**
     * Fetches and renders a section from the server.
     * @param {string} url
     * @param {Event} [event]
     * @param {string} [focusSelector=null]
     */
    static renderSectionFromFetch(url, event, focusSelector) {
        fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
                const html = responseText;
                FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
                FacetFiltersForm.renderFilters(html, event, focusSelector);
                FacetFiltersForm.renderProductGridContainer(html);
                FacetFiltersForm.renderProductCount(html);
                if (typeof initializeScrollAnimationTrigger === 'function')
                    initializeScrollAnimationTrigger(html.innerHTML);
            });
    }

    /**
     * Renders a section from the cache.
     * @param {Function} filterDataUrl
     * @param {Event} [event]
     * @param {string} [focusSelector=null]
     */
    static renderSectionFromCache(filterDataUrl, event, focusSelector) {
        const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
        FacetFiltersForm.renderFilters(html, event, focusSelector);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    }

    /**
     * Updates the product grid container with new HTML.
     * @param {string} html
     */
    static renderProductGridContainer(html) {
        document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
            .parseFromString(html, 'text/html')
            .getElementById('ProductGridContainer').innerHTML;

        document
            .getElementById('ProductGridContainer')
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => {
                element.classList.add('scroll-trigger--cancel');
            });

        if (document.querySelector('quick-add-drawer'))
            document.querySelector('quick-add-drawer').setQuickAddLinkClicks();
    }

    /**
     * Updates the product count display with new HTML.
     * @param {string} html
     */
    static renderProductCount(html) {
        const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
        const container = document.getElementById('ProductCount');
        const mainCollectionContainer = document.getElementById('main-collection-product__container');

        if (mainCollectionContainer) {
            mainCollectionContainer.classList.remove('main-collection-product--loading');
        }
        container.innerHTML = count;

        const loadingSpinner = document.querySelector('.global-loading');
        loadingSpinner.classList.add('hidden');
    }

    /**
     * Renders the filter UI from new HTML, updating only changed elements.
     * @param {string} html
     * @param {Event} [event]
     * @param {string} [focusSelector=null]
     */
    static renderFilters(html, event, focusSelector = null) {
        console;
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
            '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersQuickFilter'
        );
        const facetDetailsElementsFromDom = document.querySelectorAll(
            '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersQuickFilter'
        );

        // Remove facets that are no longer returned from the server
        Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
            if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
                currentElement.remove();
            }
        });

        const matchesId = (element) => {
            const jsFilter = event ? event.target.closest('.js-filter') : undefined;
            return jsFilter ? element.id === jsFilter.id : false;
        };
        const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
        const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

        facetsToRender.forEach((elementToRender, index) => {
            const currentElement = document.getElementById(elementToRender.id);
            // Element already rendered in the DOM so just update the innerHTML
            if (currentElement) {
                document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
            } else {
                if (index > 0) {
                    const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
                    // Same facet type (eg horizontal/vertical or drawer/mobile)
                    if (elementToRender.className === previousElementClassName) {
                        document.getElementById(previousElementId).after(elementToRender);
                        return;
                    }
                }

                if (elementToRender.parentElement) {
                    document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
                }
            }
        });

        FacetFiltersForm.renderAdditionalElements(parsedHTML);

        if (countsToRender) {
            const closestJSFilterID = event.target.closest('.js-filter').id;

            if (closestJSFilterID) {
                FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
                FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));
            }
        }

        // Restore focus after DOM update
        if (focusSelector) {
            setTimeout(() => {
                const el = document.querySelector(focusSelector);
                if (el) el.focus();
            }, 0);
        }
    }

    /**
     * Renders additional elements (mobile triggers, sorting) from new HTML.
     * @param {Document} html
     */
    static renderAdditionalElements(html) {
        const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__disclosure', '.sorting'];

        mobileElementSelectors.forEach((selector) => {
            if (!html.querySelector(selector)) return;
            document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
        });

        document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
    }

    /**
     * Renders facet counts for desktop filters.
     * @param {Element} source
     * @param {Element} target
     */
    static renderCounts(source, target) {
        const targetHeaderElement = target.querySelector('.facets__header');
        const sourceHeaderElement = source.querySelector('.facets__header');

        if (sourceHeaderElement && targetHeaderElement) {
            targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
        }

        const targetWrapElement = target.querySelector('.facets-wrap');
        const sourceWrapElement = source.querySelector('.facets-wrap');

        if (sourceWrapElement && targetWrapElement) {
            targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
        }
    }

    /**
     * Renders facet counts for mobile filters.
     * @param {Element} source
     * @param {Element} target
     */
    static renderMobileCounts(source, target) {
        const targetFacetsList = target.querySelector('.mobile-facets__list');
        const sourceFacetsList = source.querySelector('.mobile-facets__list');

        if (sourceFacetsList && targetFacetsList) {
            targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
        }
    }

    /**
     * Updates the browser URL hash with the current search parameters.
     * @param {string} searchParams
     */
    static updateURLHash(searchParams) {
        history.pushState(
            { searchParams },
            '',
            `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
        );
    }

    /**
     * Returns the section IDs for the product grid.
     * @returns {Array<{section: string}>}
     */
    static getSections() {
        return [
            {
                section: document.getElementById('product-grid').dataset.id,
            },
        ];
    }

    /**
     * Creates a URLSearchParams string from a form.
     * @param {HTMLFormElement} form
     * @returns {string}
     */
    createSearchParams(form) {
        const formData = new FormData(form);
        return new URLSearchParams(formData).toString();
    }

    /**
     * Handles form submission and triggers page update.
     * @param {string} searchParams
     * @param {Event} event
     * @param {string} [focusSelector=null]
     */
    onSubmitForm(searchParams, event, focusSelector) {
        FacetFiltersForm.renderPage(searchParams, event, true, focusSelector);
    }

    /**
     * Returns a CSS selector for the focused element.
     * @param {Element} element
     * @returns {string|null}
     */
    getFocusSelector(element) {
        if (element && element.id) {
            // Use CSS.escape if available to handle special characters in id
            if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
                return `#${CSS.escape(element.id)}`;
            } else {
                // Fallback: escape dots only
                return `#${element.id.replace(/\./g, '\\.')}`;
            }
        } else if (element && element.name) {
            return `[name='${element.name}']`;
        } else if (element && element.classList && element.classList.length > 0) {
            return '.' + Array.from(element.classList).join('.');
        }
        return null;
    }

    /**
     * Handles debounced form input and click events for filters.
     * @param {Event} event
     */
    onSubmitHandler(event) {
        event.preventDefault();
        // Capture focused element before re-render
        const focusSelector = this.getFocusSelector(document.activeElement);
        console.log('focusSelector', focusSelector);
        const sortFilterForms = document.querySelectorAll('facet-filters-form form');
        if (event.srcElement.className == 'mobile-facets__checkbox') {
            const searchParams = this.createSearchParams(event.target.closest('form'));
            this.onSubmitForm(searchParams, event, focusSelector);
        } else {
            const forms = [];
            const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

            sortFilterForms.forEach((form) => {
                if (!isMobile) {
                    if (
                        form.id === 'FacetSortForm' ||
                        form.id === 'FacetFiltersForm' ||
                        form.id === 'FacetSortDrawerForm'
                    ) {
                        const noJsElements = document.querySelectorAll('.no-js-list');
                        noJsElements.forEach((el) => el.remove());
                        forms.push(this.createSearchParams(form));
                    }
                } else if (form.id === 'FacetFiltersFormMobile') {
                    forms.push(this.createSearchParams(form));
                }
            });
            this.onSubmitForm(forms.join('&'), event, focusSelector);
        }
    }

    /**
     * Handles clicks on active filter remove links.
     * @param {Event} event
     */
    onActiveFilterClick(event) {
        event.preventDefault();
        FacetFiltersForm.toggleActiveFacets();
        // Capture focused element before re-render
        const focusSelector = this.getFocusSelector(document.activeElement);
        console.log('focusSelector', focusSelector);
        const url =
            event.currentTarget.href.indexOf('?') == -1
                ? ''
                : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
        FacetFiltersForm.renderPage(url, event, true, focusSelector);
    }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 * PriceRange handles the min/max price filter UI and validation.
 * @extends HTMLElement
 */
class PriceRange extends HTMLElement {
    /**
     * Initializes event listeners for price range inputs.
     */
    constructor() {
        super();
        this.querySelectorAll('input').forEach((element) =>
            element.addEventListener('change', this.onRangeChange.bind(this))
        );
        this.setMinAndMaxValues();
    }

    /**
     * Handles changes to price range inputs and enforces valid values.
     * @param {Event} event
     */
    onRangeChange(event) {
        this.adjustToValidValues(event.currentTarget);
        this.setMinAndMaxValues();
    }

    /**
     * Sets min/max attributes on price inputs based on current values.
     */
    setMinAndMaxValues() {
        const inputs = this.querySelectorAll('input');
        const minInput = inputs[0];
        const maxInput = inputs[1];
        if (maxInput.value) minInput.setAttribute('max', maxInput.value);
        if (minInput.value) maxInput.setAttribute('min', minInput.value);
        if (minInput.value === '') maxInput.setAttribute('min', 0);
        if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
    }

    /**
     * Adjusts input value to be within min/max bounds.
     * @param {HTMLInputElement} input
     */
    adjustToValidValues(input) {
        const value = Number(input.value);
        const min = Number(input.getAttribute('min'));
        const max = Number(input.getAttribute('max'));

        if (value < min) input.value = min;
        if (value > max) input.value = max;
    }
}

customElements.define('price-range', PriceRange);

/**
 * FacetRemove handles the remove button for active filters.
 * @extends HTMLElement
 */
class FacetRemove extends HTMLElement {
    /**
     * Initializes event listeners for the remove link.
     */
    constructor() {
        super();
        const facetLink = this.querySelector('a');
        facetLink.setAttribute('role', 'button');
        facetLink.addEventListener('click', this.closeFilter.bind(this));
        facetLink.addEventListener('keyup', (event) => {
            event.preventDefault();
            if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
        });
    }

    /**
     * Handles closing/removing the active filter.
     * @param {Event} event
     */
    closeFilter(event) {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
    }
}

customElements.define('facet-remove', FacetRemove);
